// ProfilePage.tsx
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { ManageAddressesPage } from './ManageAddressesPage';
import  MyOrders  from './MyOrders';

interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile?: string;
  profilePictureUrl?: string;
}

interface ProfilePageProps {
  onLogout: () => void;
  onProfileUpdate: (user: UserProfile) => void;
}

export const ProfilePage: React.FC<ProfilePageProps> = ({ onLogout, onProfileUpdate }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<'profile' | 'addresses' | 'payments' | 'mystuff' | 'myorders'>('profile'); 

  const [formData, setFormData] = useState<UserProfile>({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    profilePictureUrl: '/default-avatar.png',
  });

  const [isEditingPersonalInfo, setIsEditingPersonalInfo] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingMobile, setIsEditingMobile] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const getUserId = (): string | null => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        return parsedUser.id;
      } catch (e) {
        console.error("Failed to parse user from local storage", e);
        return null;
      }
    }
    return null;
  };

  useEffect(() => {
    const userId = getUserId();
    if (!userId) {
      setError("User not logged in or ID not found.");
      setLoading(false);
      return;
    }

    const fetchUserProfile = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error("You are not logged in. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await axios.get<UserProfile>(
          `https://ayushvatika.com/api/auth/profile`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        setUser(response.data);
        setFormData(response.data);
      } catch (error: any) {
        console.error("Failed to fetch user profile:", error);
        setError("Failed to fetch user profile.");
        toast.error("Failed to fetch user profile: " + (error.response?.data?.message || error.message));
      } finally {
        setLoading(false);
      }
    };
  
    fetchUserProfile();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, profilePictureUrl: reader.result as string }));
        setIsEditingPersonalInfo(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSave = async (section: 'personal' | 'email' | 'mobile') => {
    if (!user) return;

    try {
      setLoading(true);
      const updatedData: Partial<UserProfile> = { id: user.id };

      if (section === "personal") {
        updatedData.firstName = formData.firstName ?? "";
        updatedData.lastName = formData.lastName ?? "";
        updatedData.profilePictureUrl = formData.profilePictureUrl ?? "";
      } else if (section === "email") {
        updatedData.email = formData.email ?? "";
      } else if (section === "mobile") {
        updatedData.mobile = formData.mobile ?? "";
      }

      const token = localStorage.getItem("token");
      const response = await axios.put<UserProfile>(
        "https://ayushvatika.com/api/auth/profile",
        updatedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUser(response.data);
      onProfileUpdate(response.data);

      localStorage.setItem(
        "user",
        JSON.stringify({
          ...JSON.parse(localStorage.getItem("user") || "{}"),
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          email: response.data.email,
          mobile: response.data.mobile,
          profilePictureUrl: response.data.profilePictureUrl,
          picture: response.data.profilePictureUrl 
        })
      );

      window.dispatchEvent(new Event('userDataUpdated'));

      if (section === "personal") setIsEditingPersonalInfo(false);
      else if (section === "email") setIsEditingEmail(false);
      else if (section === "mobile") setIsEditingMobile(false);

      toast.success("Profile updated successfully!");
    } catch (err: any) {
      console.error(`Failed to update ${section} profile:`, err);
      toast.error(err.response?.data?.message || `Failed to update ${section} profile.`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (section: 'personal' | 'email' | 'mobile') => {
    if (user) {
      if (section === 'personal') {
        setFormData(prev => ({
          ...prev,
          firstName: user.firstName,
          lastName: user.lastName,
          profilePictureUrl: user.profilePictureUrl,
        }));
        setIsEditingPersonalInfo(false);
      } else if (section === 'email') {
        setFormData(prev => ({ ...prev, email: user.email }));
        setIsEditingEmail(false);
      } else if (section === 'mobile') {
        setFormData(prev => ({ ...prev, mobile: user.mobile }));
        setIsEditingMobile(false);
      }
    }
  };

  const handleNavClick = (section: typeof activeSection) => {
    setActiveSection(section);
    setIsMobileMenuOpen(false); 
  };

  const NavItem = ({ section, label, icon }: { section: typeof activeSection, label: string, icon: React.ReactNode }) => (
    <li 
      className={`flex items-center p-3 rounded-lg cursor-pointer whitespace-nowrap transition-all ${
        activeSection === section 
          ? 'bg-emerald-50 border-l-4 border-emerald-600 text-emerald-700 font-semibold shadow-sm' 
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
      }`}
      onClick={() => handleNavClick(section)}
    >
      <div className={`mr-3 ${activeSection === section ? 'text-emerald-700' : 'text-gray-400'}`}>
        {icon}
      </div>
      {label}
    </li>
  );

  const getPageTitle = () => {
    switch(activeSection) {
      case 'profile': return 'Profile Info';
      case 'myorders': return 'My Orders';
      case 'addresses': return 'Addresses';
      case 'payments': return 'Payments';
      case 'mystuff': return 'My Stuff';
      default: return 'Profile';
    }
  };

  if (loading) return <div className="flex justify-center items-center h-screen bg-gray-100 text-emerald-700 animate-pulse">Loading profile...</div>;
  if (error) return <div className="flex flex-col justify-center items-center h-screen bg-gray-100 text-red-600"><p>{error}</p><button onClick={onLogout} className="mt-4 px-6 py-2 bg-emerald-600 text-white rounded-lg">Go to Login</button></div>;
  if (!user) return <div className="flex justify-center items-center h-screen bg-gray-100 text-gray-700">No user data available.</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col md:flex-row md:p-4 font-inter gap-4 relative">
      
      {/* --- MOBILE HEADER STRIP --- */}
      <div className="md:hidden flex justify-between items-center bg-white h-14 px-4 border-b sticky top-0 z-20">
        <h2 className="text-lg font-bold text-gray-800">{getPageTitle()}</h2>
        <button 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
        >
          {isMobileMenuOpen ? (
             <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          ) : (
             <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path></svg>
          )}
        </button>
      </div>

      {/* --- MOBILE OVERLAY --- */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 top-[120px] z-30 md:hidden bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}

      {/* --- SIDEBAR MENU --- */}
      <div className={`
          fixed md:sticky             
          z-40 md:z-0
          top-[120px] md:top-32       
          md:self-start               
          right-0 md:left-0        
          h-[calc(100vh-60px)] md:h-fit 
          w-[75%] md:w-1/4         
          bg-white md:rounded-xl shadow-2xl md:shadow-lg 
          p-4 md:p-6 flex flex-col transition-transform duration-300 ease-in-out
          border-l md:border-none
          ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}
      `}>
        
        {/* Sidebar Content - REMOVED IMAGE, KEPT NAME ONLY */}
        <div className="flex flex-col items-center gap-2 mb-6 border-b pb-4 pt-2">
          {/* Profile Picture removed from here */}
          <div className="text-center">
             <p className="text-3xl font-bold text-gray-800 ">{formData.firstName} {formData.lastName}</p>
             <p className="text-xs text-gray-500 truncate max-w-[180px]">{formData.email}</p>
          </div>
        </div>

        <nav className="w-full overflow-y-auto custom-scrollbar flex-1">
          <ul className="flex flex-col space-y-1">
            <NavItem section="profile" label="Profile Info" icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>} />
            <NavItem section="myorders" label="My Orders" icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path></svg>} />
            <NavItem section="addresses" label="Addresses" icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.828 0L6.343 16.657M17.657 16.657A8 8 0 1118 8a8 8 0 01-1.071 3.514L12 21l-4.929-7.371A8 8 0 0118 8z"></path></svg>} />
            <NavItem section="payments" label="Payments" icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path></svg>} />
            <NavItem section="mystuff" label="My Stuff" icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path></svg>} />

            <li className="flex items-center p-3 text-red-600 hover:bg-red-50 rounded-lg cursor-pointer transition mt-auto pt-4 border-t whitespace-nowrap" onClick={onLogout}>
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
              Logout
            </li>
          </ul>
        </nav>
      </div>

      {/* 2. Main Content Area */}
      <div className="w-full md:w-3/4 flex-1 p-4 md:p-0">
        {activeSection === 'profile' && (
          <div className="bg-white rounded-xl shadow-lg p-4 md:p-8 space-y-6 md:space-y-8 animate-fade-in">
            {/* Personal Information */}
            <div className="border border-gray-200 rounded-lg p-4 md:p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg md:text-xl font-semibold text-gray-800">Personal Information</h3>
                {!isEditingPersonalInfo ? (
                  <button onClick={() => setIsEditingPersonalInfo(true)} className="text-blue-600 hover:underline font-medium text-sm">Edit</button>
                ) : (
                  <div className="space-x-2">
                    <button onClick={() => handleSave('personal')} className="px-3 py-1 bg-emerald-600 text-white rounded text-sm" disabled={loading}>Save</button>
                    <button onClick={() => handleCancel('personal')} className="px-3 py-1 bg-gray-200 text-gray-800 rounded text-sm" disabled={loading}>Cancel</button>
                  </div>
                )}
              </div>

              {/* Profile Picture in Main Content (Kept as requested) */}
              <div className="flex items-center gap-4 mb-6">
                  <div className="relative group">
                    <img 
                        src={formData.profilePictureUrl || '/default-avatar.png'} 
                        className="w-20 h-20 rounded-full object-cover border border-gray-200"
                        onError={(e) => { e.currentTarget.src = '/default-avatar.png'; }}
                    />
                    <button 
                        onClick={handleImageUploadClick}
                        className="absolute bottom-0 -right-2 bg-white text-gray-700 p-1.5 rounded-full shadow border border-gray-200 hover:bg-gray-50 transition"
                        title="Change Photo"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                    </button>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Profile Picture</p>
                    <p className="text-xs text-gray-400">Click icon to update</p>
                    <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*" />
                  </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-500 text-xs uppercase font-bold mb-1">First Name</label>
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} readOnly={!isEditingPersonalInfo} className={`w-full border rounded-lg px-3 py-2 text-sm ${isEditingPersonalInfo ? 'bg-white focus:ring-emerald-500' : 'bg-gray-50 text-gray-600'}`} />
                </div>
                <div>
                  <label className="block text-gray-500 text-xs uppercase font-bold mb-1">Last Name</label>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} readOnly={!isEditingPersonalInfo} className={`w-full border rounded-lg px-3 py-2 text-sm ${isEditingPersonalInfo ? 'bg-white focus:ring-emerald-500' : 'bg-gray-50 text-gray-600'}`} />
                </div>
              </div>
            </div>

            {/* Email Address */}
            <div className="border border-gray-200 rounded-lg p-4 md:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg md:text-xl font-semibold text-gray-800">Email Address</h3>
                {!isEditingEmail ? (
                  <button onClick={() => setIsEditingEmail(true)} className="text-blue-600 hover:underline font-medium text-sm">Edit</button>
                ) : (
                  <div className="space-x-2">
                    <button onClick={() => handleSave('email')} className="px-3 py-1 bg-emerald-600 text-white rounded text-sm" disabled={loading}>Save</button>
                    <button onClick={() => handleCancel('email')} className="px-3 py-1 bg-gray-200 text-gray-800 rounded text-sm" disabled={loading}>Cancel</button>
                  </div>
                )}
              </div>
              <input type="email" name="email" value={formData.email} onChange={handleChange} readOnly={!isEditingEmail} className={`w-full border rounded-lg px-3 py-2 text-sm ${isEditingEmail ? 'bg-white focus:ring-emerald-500' : 'bg-gray-50 text-gray-600'}`} />
            </div>

            {/* Mobile Number */}
            <div className="border border-gray-200 rounded-lg p-4 md:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg md:text-xl font-semibold text-gray-800">Mobile Number</h3>
                {!isEditingMobile ? (
                  <button onClick={() => setIsEditingMobile(true)} className="text-blue-600 hover:underline font-medium text-sm">Edit</button>
                ) : (
                  <div className="space-x-2">
                    <button onClick={() => handleSave('mobile')} className="px-3 py-1 bg-emerald-600 text-white rounded text-sm" disabled={loading}>Save</button>
                    <button onClick={() => handleCancel('mobile')} className="px-3 py-1 bg-gray-200 text-gray-800 rounded text-sm" disabled={loading}>Cancel</button>
                  </div>
                )}
              </div>
              <input type="text" name="mobile" value={formData.mobile ?? ""} onChange={handleChange} readOnly={!isEditingMobile} className={`w-full border rounded-lg px-3 py-2 text-sm ${isEditingMobile ? 'bg-white focus:ring-emerald-500' : 'bg-gray-50 text-gray-600'}`} />
            </div>
          </div>
        )}

        {/* Dynamic Section Rendering */}
        {activeSection === 'myorders' && user && <MyOrders userId={user.id} />}
        {activeSection === 'addresses' && user && <ManageAddressesPage userId={user.id} />}
        
        {activeSection === 'payments' && (
          <div className="bg-white rounded-xl shadow-lg p-8 h-full flex flex-col justify-center items-center text-center">
             <div className="bg-purple-50 p-4 rounded-full mb-4">
                <svg className="w-10 h-10 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path></svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Saved Payments</h3>
            <p className="text-gray-500">Manage your saved cards and UPI IDs.<br/><span className="text-sm font-semibold text-emerald-600">Coming Soon!</span></p>
          </div>
        )}
        
        {activeSection === 'mystuff' && (
          <div className="bg-white rounded-xl shadow-lg p-8 h-full flex flex-col justify-center items-center text-center">
             <div className="bg-yellow-50 p-4 rounded-full mb-4">
                <svg className="w-10 h-10 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path></svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">My Stuff</h3>
            <p className="text-gray-500">Your wishlist, reviews, and more.<br/><span className="text-sm font-semibold text-emerald-600">Coming Soon!</span></p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;